import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ColorPicker from '@/components/ui/ColorPicker';
import { ColorVariant } from '@/components/ui/SidebarFilterButtons';

interface OnboardingAvatarContentProps {
  setDescription: (description: string) => void;
  setStyle: (style: string) => void;
}

const colorNameMap: Record<ColorVariant, string> = {
  purple: 'purple',
  green: 'green', 
  amber: 'orange',
  blue: 'blue',
  red: 'red',
  indigo: 'indigo',
  gray: 'gray',
  orange: 'orange',
  brown: 'brown',
  white: 'white',
  black: 'black'
};

const OnboardingAvatarContent: React.FC<OnboardingAvatarContentProps> = ({ 
  setDescription,
  setStyle
}) => {
  const [favoriteAnimal, setFavoriteAnimal] = useState('');
  const [selectedColor, setSelectedColor] = useState<ColorVariant>('purple');

  // Update parent component whenever values change
  useEffect(() => {
    if (favoriteAnimal.trim()) {
      // Create the combined prompt: "cute and lovable [color] [animal]"
      const colorName = colorNameMap[selectedColor];
      const finalDescription = `cute and lovable ${colorName} ${favoriteAnimal.trim()}`;
      
      // Attach to the usual avatar prompt elegantly - emphasize ultra-realistic
      const fullPrompt = `Create an ultra-realistic avatar of a ${finalDescription}, high quality photography, realistic lighting`;
      
      if (setDescription) {
        setDescription(fullPrompt);
      }
    } else {
      if (setDescription) {
        setDescription('');
      }
    }
    
    // Always set style to photo for onboarding
    if (setStyle) {
      setStyle('photo');
    }
  }, [favoriteAnimal, selectedColor, setDescription, setStyle]);

  const handleAnimalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFavoriteAnimal(e.target.value);
  };

  const handleColorChange = (color: ColorVariant) => {
    setSelectedColor(color);
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      {/* Favorite Animal Input */}
      <motion.div 
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.3 }}
        className="space-y-2"
      >
        <Label htmlFor="animal" className="text-sm font-medium">
          What's your favorite animal? 🐾
        </Label>
        <Input
          id="animal"
          placeholder="e.g., dog, cat, wolf, eagle, dolphin..."
          value={favoriteAnimal}
          onChange={handleAnimalChange}
          className="text-lg py-3"
          autoFocus
        />
      </motion.div>

      {/* Color Picker */}
      <motion.div 
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.4 }}
        className="space-y-3"
      >
        <Label className="text-sm font-medium">
          Pick your favorite color 🌈
        </Label>
        <div className="flex justify-start">
          <ColorPicker
            selectedColor={selectedColor}
            onColorChange={handleColorChange}
            className="w-fit"
          />
        </div>
      </motion.div>

      {/* Preview Text */}
      {favoriteAnimal.trim() && (
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
          className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/30 dark:to-blue-950/30 p-4 rounded-lg border border-purple-200/50 dark:border-purple-800/30"
        >
          <p className="text-sm text-gray-700 dark:text-gray-300">
            <span className="font-medium">Preview:</span> We'll create a {colorNameMap[selectedColor]} {favoriteAnimal} avatar for you! ✨
          </p>
        </motion.div>
      )}
    </motion.div>
  );
};

export default OnboardingAvatarContent; 